import {
  ContractorAdditionalSettingsResponse,
  LockData,
  Owner as ContractorOwner,
} from "@/models/api/administration/contractors/ContractorAdditionalSettingsResponse";
import { Owner } from "@/models/api/Owner";
import {
  editContractorComment,
  getContractorAdditionalSettings,
  saveSummaryPrintSetting,
  editContractorOwners,
  lockContractor,
  unlockContractor,
  saveShowExchangePurchaseInformationOnInvoice,
  saveContractorProviderIdentity,
  saveSymbolsJPK,
  savePaymentMethodRestriction,
} from "@/requests/administration/contractors/contractorAdditionalSettingsAdministration";
import { OWNER_TYPES } from "@/static/ownerTypes";
import { moduleActionContext, moduleGetterContext } from "@/store";
import { defineModule } from "direct-vuex";
import { ContractorProviderIdentity } from "@/models/api/administration/contractors/ContractorProviderIdentity";

export interface AdministrationContractorSettingsStateInterface {
  contractorTraders: Array<ContractorOwner>;
  contractorCashiers: Array<ContractorOwner>;
  summaryPrintSetting: boolean;
  isPaymentMethodRestrictedSetting: boolean;
  showExchangePurchaseInformationOnInvoice: boolean;
  selectedCashier: null | Owner;
  selectedTrader: null | Owner;
  selectedTraderShare: number;
  selectedCashierShare: number;
  lockData: LockData;
  providerIdentity: string | null;
  supplierAccount: string | null;
  symbolsJpk: string | null;
}

export const AdministrationContractorSettingsState: AdministrationContractorSettingsStateInterface = {
  contractorTraders: new Array<ContractorOwner>(),
  contractorCashiers: new Array<ContractorOwner>(),
  summaryPrintSetting: false,
  isPaymentMethodRestrictedSetting: false,
  showExchangePurchaseInformationOnInvoice: false,
  selectedCashier: null,
  selectedTrader: null,
  selectedTraderShare: 0,
  selectedCashierShare: 0,
  lockData: new LockData(),
  providerIdentity: null,
  supplierAccount: null,
  symbolsJpk: null,
};

const AdministrationContractorSettingsModule = defineModule({
  state: AdministrationContractorSettingsState as AdministrationContractorSettingsStateInterface,
  namespaced: true as const,
  getters: {
    sumCashiersShared(...args): number {
      const { state } = _moduleGetterContext(args);
      const result = state.contractorCashiers.reduce((sum, current) => sum + current.share, 0);
      if (result < 0) return 0;
      return result;
    },
    sumTradersShared(...args): number {
      const { state } = _moduleGetterContext(args);
      const result = state.contractorTraders.reduce((sum, current) => sum + current.share, 0);
      if (result < 0) return 0;
      return result;
    },
    availableCashiersShare(...args): number {
      const { getters } = _moduleGetterContext(args);
      return 100 - getters.sumCashiersShared;
    },
    availableTradersShare(...args): number {
      const { getters } = _moduleGetterContext(args);
      return 100 - getters.sumTradersShared;
    },
  },
  mutations: {
    setContractorAdditionalSettings(state, param: ContractorAdditionalSettingsResponse) {
      state.lockData = param.lockData;
      param.owners.forEach((item) => (item.share = item.share * 100));
      state.contractorCashiers = param.owners.filter((item) => item.type == OWNER_TYPES.Cashier);
      state.contractorTraders = param.owners.filter((item) => item.type == OWNER_TYPES.Trader);
      state.summaryPrintSetting = param.summaryPrint;
      state.isPaymentMethodRestrictedSetting = param.isPaymentMethodRestricted;
      state.showExchangePurchaseInformationOnInvoice = param.showExchangePurchaseInformationOnInvoice;
      state.supplierAccount = param.supplierAccount;
      state.providerIdentity = param.providerIdentity;
      state.symbolsJpk = param.symbolsJPK;
    },
    setSelectedCashier(state, param: Owner) {
      state.selectedCashier = param;
    },
    setSelectedTrader(state, param: Owner) {
      state.selectedTrader = param;
    },
    setSymbolsJpk(state, param: string) {
      state.symbolsJpk = param;
    },
    setSelectedCashierShare(state, param: number) {
      state.selectedCashierShare = param;
    },
    setSelectedTraderShare(state, param: number) {
      state.selectedTraderShare = param;
    },
    setSupplierAccount(state, param: string) {
      state.supplierAccount = param;
    },
    setProviderIdentity(state, param: string) {
      state.providerIdentity = param;
    },
    setSummaryPrint(state, param: boolean) {
      state.summaryPrintSetting = param;
    },
    setIsPaymentMethodRestricted(state, param: boolean) {
      state.isPaymentMethodRestrictedSetting = param;
    },
    setShowExchangePurchaseInformationOnInvoice(state, param: boolean) {
      state.showExchangePurchaseInformationOnInvoice = param;
    },
    removeTrader(state, item: ContractorOwner) {
      state.contractorTraders.splice(state.contractorTraders.indexOf(item), 1);
    },
    removeCashier(state, item: ContractorOwner) {
      state.contractorCashiers.splice(state.contractorCashiers.indexOf(item), 1);
    },
    addCashier(state, item: ContractorOwner) {
      state.contractorCashiers.push(item);
    },
    addTrader(state, item: ContractorOwner) {
      state.contractorTraders.push(item);
    },
  },
  actions: {
    validateAndSetSelectedCashierShare(context, param: number) {
      const { commit, getters } = _moduleActionContext(context);
      if (param < 0) param = 0;
      if (param > getters.availableCashiersShare) param = getters.availableCashiersShare;
      commit.setSelectedCashierShare(param);
    },
    validateAndSetSelectedTraderShare(context, param: number) {
      const { commit, getters } = _moduleActionContext(context);
      if (param < 0) param = 0;
      if (param > getters.availableTradersShare) param = getters.availableTradersShare;
      commit.setSelectedTraderShare(param);
    },
    async getContractorAdditionalSettings(context) {
      const { commit, getters, rootState } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        const contractorAdditionalSettings = await getContractorAdditionalSettings(
          rootState.administration.contractors.list.selectedContractorId as string
        );
        commit.setContractorAdditionalSettings(contractorAdditionalSettings);
        commit.setSelectedTraderShare(getters.availableTradersShare);
        commit.setSelectedCashierShare(getters.availableCashiersShare);
      }
    },
    async editContractorComment(context) {
      const { state, rootState } = _moduleActionContext(context);
      if (state.lockData.comment == null || state.lockData.comment == "") return;
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        await editContractorComment(
          rootState.administration.contractors.list.selectedContractorId as string,
          state.lockData.comment
        );
      }
    },
    async savePaymentMethodRestriction(context) {
      const { state, rootState, dispatch } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        await savePaymentMethodRestriction(
          rootState.administration.contractors.list.selectedContractorId as string,
          state.isPaymentMethodRestrictedSetting
        );
        dispatch.getContractorAdditionalSettings();
      }
    },
    async saveSummaryPrint(context) {
      const { state, rootState, dispatch } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        await saveSummaryPrintSetting(
          rootState.administration.contractors.list.selectedContractorId as string,
          state.summaryPrintSetting
        );
        dispatch.getContractorAdditionalSettings();
      }
    },
    async saveShowExchangePurchaseInformationOnInvoice(context) {
      const { state, rootDispatch, rootState, dispatch } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        await saveShowExchangePurchaseInformationOnInvoice(
          rootState.administration.contractors.list.selectedContractorId as string,
          state.showExchangePurchaseInformationOnInvoice as boolean
        );
        dispatch.getContractorAdditionalSettings();
        rootDispatch.popup.addSavedOkSnackbar();
      }
    },
    async saveContractorOwners(context) {
      const { state, rootDispatch, rootState, dispatch } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        let owners = state.contractorCashiers;
        owners = owners.concat(state.contractorTraders);
        await editContractorOwners(rootState.administration.contractors.list.selectedContractorId as string, owners);
        dispatch.getContractorAdditionalSettings();
        rootDispatch.popup.addSavedOkSnackbar();
      }
    },
    async saveSymbolsJPK(context) {
      const { state, rootDispatch, rootState, dispatch } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        await saveSymbolsJPK(
          rootState.administration.contractors.list.selectedContractorId as string,
          state.symbolsJpk
        );
        dispatch.getContractorAdditionalSettings();
        rootDispatch.popup.addSavedOkSnackbar();
      }
    },
    async saveContractorProviderIdentity(context) {
      const { state, rootDispatch, rootState, dispatch } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        await saveContractorProviderIdentity(
          rootState.administration.contractors.list.selectedContractorId as string,
          {
            providerIdentity: state.providerIdentity ?? "",
            supplierAccount: state.supplierAccount ?? "",
          } as ContractorProviderIdentity
        );
        dispatch.getContractorAdditionalSettings();
        rootDispatch.popup.addSavedOkSnackbar();
      }
    },
    addCashier(context) {
      const { state, getters, commit } = _moduleActionContext(context);
      if (state.selectedCashier == null) return;
      if (state.contractorCashiers.find((v) => v.userId === state.selectedCashier?.userId) != null) return;
      commit.addCashier({
        ownerId: state.selectedCashier?.id as number,
        userId: state.selectedCashier?.userId as string,
        type: OWNER_TYPES.Cashier,
        name: state.selectedCashier?.name as string,
        share: state.selectedCashierShare,
      } as ContractorOwner);
      commit.setSelectedCashierShare(getters.availableCashiersShare);
    },
    addTrader(context) {
      const { state, getters, commit } = _moduleActionContext(context);
      if (state.selectedTrader == null) return;
      if (state.contractorTraders.find((v) => v.userId === state.selectedTrader?.userId) != null) return;
      commit.addTrader({
        ownerId: state.selectedCashier?.id as number,
        userId: state.selectedTrader?.userId as string,
        type: OWNER_TYPES.Trader,
        name: state.selectedTrader?.name as string,
        share: state.selectedTraderShare,
      } as ContractorOwner);
      commit.setSelectedTraderShare(getters.availableTradersShare);
    },
    removeCashier(context, param: ContractorOwner) {
      const { getters, commit } = _moduleActionContext(context);
      commit.removeCashier(param);
      commit.setSelectedCashierShare(getters.availableCashiersShare);
    },
    removeTrader(context, param: ContractorOwner) {
      const { getters, commit } = _moduleActionContext(context);
      commit.removeTrader(param);
      commit.setSelectedTraderShare(getters.availableTradersShare);
    },
    async lockContractor(context) {
      const { state, rootState, dispatch } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        await lockContractor(
          rootState.administration.contractors.list.selectedContractorId,
          state.lockData.comment as string
        );
        dispatch.getContractorAdditionalSettings();
      }
    },
    async unlockContractor(context) {
      const { state, rootState, dispatch } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        await unlockContractor(
          rootState.administration.contractors.list.selectedContractorId,
          state.lockData.comment as string
        );
        dispatch.getContractorAdditionalSettings();
      }
    },
  },
});

export default AdministrationContractorSettingsModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, AdministrationContractorSettingsModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, AdministrationContractorSettingsModule);
